/**
 * CREEL — intent state helpers (§6.2). No SSE (§11), so EXPIRED is READ-DRIVEN: any read of a PENDING
 * mirror doc past `expiresAt` normalizes to EXPIRED locally, without a Clavero call. These pure
 * predicates drive the drawer countdown, the confirm re-validation gate, and the assistant tools.
 */

import type { LapoOrderIntent, LapoIntentStatus } from './types.js';

/** The 10-minute Clavero intent TTL (§2.4). */
export const INTENT_TTL_MS = 10 * 60 * 1000;

/** Terminal Lapo statuses — no further transition, never re-confirmable. */
const TERMINAL: ReadonlySet<LapoIntentStatus> = new Set<LapoIntentStatus>([
  'CONFIRMED',
  'CANCELLED',
  'EXPIRED',
  'ABORTED_DRIFT',
  'NEEDS_HUMAN',
  'FAILED',
]);

export function isTerminalIntentStatus(status: LapoIntentStatus): boolean {
  return TERMINAL.has(status);
}

/**
 * The effective status at time `now`: a PENDING mirror past `expiresAt` reads as EXPIRED (read-driven
 * normalization, §6.2 step 4). A BUILDING mirror past `expiresAt` reads as FAILED — Clavero can drop a
 * build without ever flipping its status (mac-mini/Chrome died, network stall), so without this a stuck
 * build shows as an eternal "Preparing…" spinner that never resolves. Reading it FAILED stops the
 * spinner AND lets {@link getIntent}'s poll surface the failure (it synthesizes the human reason).
 * Everything else is returned unchanged.
 */
export function effectiveIntentStatus(intent: Pick<LapoOrderIntent, 'status' | 'expiresAt'>, now: number): LapoIntentStatus {
  if (intent.expiresAt > 0 && now >= intent.expiresAt) {
    if (intent.status === 'PENDING') return 'EXPIRED';
    if (intent.status === 'BUILDING') return 'FAILED';
  }
  return intent.status;
}

/** Whether an intent is still confirmable at `now` (PENDING and inside its TTL). */
export function isIntentConfirmable(intent: Pick<LapoOrderIntent, 'status' | 'expiresAt'>, now: number): boolean {
  return effectiveIntentStatus(intent, now) === 'PENDING';
}

/** Milliseconds until expiry (never negative); 0 once expired. Drives the countdown chip. */
export function msUntilExpiry(intent: Pick<LapoOrderIntent, 'expiresAt'>, now: number): number {
  return Math.max(0, intent.expiresAt - now);
}

/** Apply read-driven normalization to a mirror doc, returning a copy with EXPIRED/FAILED set when due. */
export function normalizeIntentForRead(intent: LapoOrderIntent, now: number): LapoOrderIntent {
  const status = effectiveIntentStatus(intent, now);
  return status === intent.status ? intent : { ...intent, status };
}

/**
 * Turn a FAILED intent's raw `error` (a Clavero-side string, or empty for a timed-out build) into a
 * user-facing sentence for signals, push, chat, and the drawer. Every branch reassures nothing was
 * purchased — a FAILED intent never places an order. Recognizes the common Clavero guardrail shapes and
 * falls back to passing the raw reason through so we never hide an unknown failure.
 */
export function humanizeIntentError(error?: string): string {
  const raw = (error ?? '').trim();
  if (!raw) return 'Amazon didn’t finish building this order in time. Nothing was purchased — try again.';
  // Clavero guardrail: "guardrail <control> failed: <detail>".
  const guard = /^guardrail\s+(\S+)\s+failed:?\s*(.*)$/i.exec(raw);
  if (guard) {
    const control = guard[1] ?? '';
    const detail = guard[2] ?? '';
    return `Blocked by an Amazon safety guardrail (${control})${detail ? `: ${detail}` : ''}. Nothing was purchased.`;
  }
  return `That order couldn’t be placed: ${raw}. Nothing was purchased.`;
}
